import _ from 'lodash'
import React, { useMemo, useState } from 'react'
import { num2chars, getDefaultAreasState } from '../constants/CardComponent'
import { getChildArea } from '../constants/getChildArea'
import { getGridData } from '../constants/getGridData'
import { LAYOUT_MODE, getValueForMode } from '../constants/toUnits'
import { useSelector, shallowEqual, useStore } from 'react-redux'
import { useLayoutMode } from './useLayoutMode'
import { fromJS, List, Map } from 'immutable'
import { getRootPath } from './useWidgetComponent'
import { getElementDataRoot } from '../constants/getElementDataRoot'
import { isSame } from '../components/WidgetBuilder/UI/isSame'

export const useGridSettings = (ref, PageID) => {
  const device = useLayoutMode()
  const componentStyles = useSelector((state) => {
    const isGlobalStyle = state.getIn(['project', 'Settings', 'styles', ref, 'isGlobal'])
    if (isGlobalStyle) {
      return state.getIn(['project', 'Settings', 'styles', ref], Map())
    } else {
      return state.getIn(
        PageID === 'project' ? ['project', 'Settings', 'styles', ref] : ['pages', PageID, 'Props', 'styles', ref],
        Map()
      )
    }
  })
  return useMemo(() => {
    const { columns, rows } = getGridData(componentStyles, device)
    return [columns, rows]
  }, [componentStyles, device])
}

export const getGridAreaForMode = (columns, rows, lgValue, mdValue, smValue, xsValue, ssValue, device, smlValue) => {
  const values = [lgValue, mdValue, smlValue, smValue, xsValue, ssValue]
  let valueIndex = LAYOUT_MODE.indexOf(device)
  if (valueIndex !== -1) {
    let value = values[valueIndex]
    do {
      if (typeof value !== 'undefined' && value !== null) {
        if (device === 'xs' || device === 'ss' || device === 'sm') {
          if (value && LAYOUT_MODE[valueIndex] == device) {
            return value
          }
          const areas = _.chunk(
            _.slice(
              _.concat(
                _.flatten(value?.toJS?.()),
                _.times(columns * rows, (n) => num2chars(n, false), columns)
              ),
              0,
              columns * rows
            ),
            columns
          )
          return fromJS(areas)
        }
        return value
      }
      value = values[--valueIndex]
    } while (valueIndex >= 0)
  }
  return lgValue
}

export const useGridAreaForDevice = (ref, columns, rows, PageID) => {
  const device = useLayoutMode()
  const isGlobalStyle = useSelector((state) => state.getIn(['project', 'Settings', 'styles', ref, 'isGlobal']))
  const styleBasePath = isGlobalStyle
    ? ['project', 'Settings', 'styles', ref]
    : PageID === 'project'
      ? ['project', 'Settings', 'styles', ref]
      : ['pages', PageID, 'Props', 'styles', ref]

  const getPath = (mode) => [...styleBasePath, mode, 'areas']

  const lgValue = useSelector(
    (state) =>
      state.getIn(getPath('lg'), fromJS(_.chunk(_.times(columns * rows, (n) => num2chars(n, false), columns)))),
    isSame
  )
  const mdValue = useSelector((state) => state.getIn(getPath('md'), null), isSame)
  const smlValue = useSelector((state) => state.getIn(getPath('sml'), null), isSame)
  const smValue = useSelector((state) => state.getIn(getPath('sm'), null), isSame)
  const xsValue = useSelector((state) => state.getIn(getPath('xs'), null), isSame)
  const ssValue = useSelector((state) => state.getIn(getPath('ss'), null), isSame)
  return useMemo(
    () => getGridAreaForMode(columns, rows, lgValue, mdValue, smValue, xsValue, ssValue, device, smlValue),
    [lgValue, mdValue, smValue, xsValue, ssValue, device, smlValue]
  )
}

export const useValueForDevice = (getPath, defaultValue) => {
  const device = useLayoutMode([])
  const lgValue = useSelector((state) => state.getIn(getPath('lg'), defaultValue), isSame)
  const mdValue = useSelector((state) => state.getIn(getPath('md'), null), isSame)
  const smlValue = useSelector((state) => state.getIn(getPath('sml'), null), isSame)
  const smValue = useSelector((state) => state.getIn(getPath('sm'), null), isSame)
  const xsValue = useSelector((state) => state.getIn(getPath('xs'), null), isSame)
  const ssValue = useSelector((state) => state.getIn(getPath('ss'), null), isSame)

  const value = useMemo(() => {
    const values = [lgValue, mdValue, smlValue, smValue, xsValue, ssValue]
    let valueIndex = LAYOUT_MODE.indexOf(device)
    if (valueIndex !== -1) {
      let value = values[valueIndex]
      do {
        if (typeof value !== 'undefined' && value !== null) {
          return value
        }
        value = values[--valueIndex]
      } while (valueIndex >= 0)
    }
    return lgValue
  }, [lgValue, mdValue, smValue, xsValue, ssValue, device, smlValue])

  return value
}

const getValueUntil = (_values, mode, defVal) => {
  const values = _.values(_values)
  let valueIndex = LAYOUT_MODE.indexOf(mode)
  if (valueIndex !== -1) {
    let value = values[valueIndex]
    do {
      if (typeof value !== 'undefined' && value !== null) {
        return value
      }
      value = values[--valueIndex]
    } while (valueIndex >= 0)
  } else {
    return _.first(values) ?? defVal
  }
}

export const useGridAreaStyleRules = (ref, PageID, uid) => {
  const PageUID = useSelector((state) =>
    state
      .getIn(['project', 'Pages'], List())
      .find((page) => page?.get?.('_id') === PageID)
      ?.get?.('uid')
  )
  const rules = useSelector(
    (state) =>
      state.getIn(['rules', PageUID], Map()).reduce((r, v, k) => {
        if (_.startsWith(k, uid)) {
          return r.set(k, v)
        } else {
          return r
        }
      }, Map()),
    isSame
  )
  const layoutMode = useLayoutMode()
  const styles = useSelector((state) => state.getIn([...getElementDataRoot(PageID), 'styles', ref], Map()))
  const IsConfiguredDevice = useSelector((state) =>
    state.getIn([...getElementDataRoot(PageID), 'IsConfiguredDevice'], Map())
  )

  return useMemo(() => {
    const stripRules = rules.reduce(
      (ret, v, k) => ({
        ...ret,
        [k.replace(`${uid}-`, '')]: v.toList().toJS(),
      }),
      {}
    )

    const configuredDev = {
      ...IsConfiguredDevice.toJS(),
      lg: true,
    }

    const { areas } = getGridData(styles, layoutMode)
    const cols = _.uniq(_.flatten(areas.toJS()))
    _.each(cols, (col) => {
      stripRules[col] = stripRules[col] || []
      stripRules[col].push('grow')
    })

    styles.get('isGrow', Map()).map((keys, areaKey) => {
      if (String(areaKey).indexOf('ft__') !== -1) {
        const area = String(areaKey).replace('ft__', '')
        const lg = keys.get('lg', true)
        const md = keys.get('md', true)
        const sml = keys.get('sml', true)
        const sm = keys.get('sm', true)
        const xs = keys.get('xs', true)
        const ss = keys.get('ss', true)
        const rawIsGrow = [lg, md, sm, xs, ss, sml]
        let isGrow = keys.get(layoutMode)

        if (!configuredDev[layoutMode]) {
          for (let i = LAYOUT_MODE.indexOf(layoutMode); i >= 0; i--) {
            if (configuredDev[LAYOUT_MODE[i]]) {
              isGrow = rawIsGrow[i]
              break
            }
          }
        }
        // const isGrow = keys.get(layoutMode, false)
        stripRules[area] = stripRules[area] ?? []
        if (isGrow) {
          stripRules[area].push('grow')
          _.compact(stripRules[area])
        } else {
          stripRules[area] = _.filter(stripRules[area], (v) => v !== 'grow')
        }
      }
    })

    styles.get('isHidden', Map()).map((keys, areaKey) => {
      if (String(areaKey).indexOf('ft__') !== -1) {
        const area = String(areaKey).replace('ft__', '')
        const lg = keys.get('lg', false)
        const md = keys.get('md')
        const sml = keys.get('sml')
        const sm = keys.get('sm')
        const xs = keys.get('xs')
        const ss = keys.get('ss')

        const rawIsHidden = [lg, md, sml, sm, xs, ss]
        let isHidden = keys.get(layoutMode)

        if (!configuredDev[layoutMode]) {
          for (let i = LAYOUT_MODE.indexOf(layoutMode); i >= 0; i--) {
            if (configuredDev[LAYOUT_MODE[i]]) {
              isHidden = rawIsHidden[i]
              break
            }
          }
        }
        // const isHidden = keys.get(layoutMode, false)
        stripRules[area] = stripRules[area] ?? []
        if (isHidden) {
          stripRules[area].push('hide')
        }
      }
    })
    return stripRules
  }, [rules, styles, layoutMode, IsConfiguredDevice])
}

export const useStripChildAreas = (PageID, uid, mode) => {
  const store = useStore()
  const children = useSelector((state) =>
    state.getIn([...getElementDataRoot(PageID), 'elements', uid, 'children'], List())
  )
  return useMemo(() => {
    let state = store.getState()
    return _.map(children.toJS(), (childUID) => getChildArea(state, childUID, mode))
  }, [children, mode])
}

export function useChildrenArea(PageID, uid, mode) {
  const store = useStore()
  const child = useSelector((state) =>
    state.getIn([...getElementDataRoot(PageID), 'elements', uid, 'children'], List())
  )

  return useMemo(() => {
    const state = store.getState()
    let children = {}
    _.each(child.toJS(), (key) => {
      let child = state.getIn([...getElementDataRoot(PageID), 'elements', key], Map())
      let area
      if (child.get('type') == 'Stack' && String(key).indexOf('fit_') > -1) {
        area = child.getIn(['props', mode, 'gridArea'])
      } else {
        area = getValueForMode(child, mode, ['props'], ['gridArea'])
      }

      children[area] = children[area] ?? []
      children[area].push(key)
    })
    return children
  }, [child, mode])
}
